// todo: create supabase edge function for this
const url = process.env.REACT_APP_SUPABASE_URL;
const anonAuthHeaders = {
  "Content-Type": "application/json",
  Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON}`,
};

/**
 * Verifies the admin password on the server. The bcrypt hash stays in Supabase secrets only.
 * @param {string} password - Plaintext password from the admin login form
 * @returns {Promise<{ ok: true } | { ok: false, error?: string }>}
 */
export const verifyAdminPassword = async (password) => {
  try {
    const response = await fetch(`${url}/functions/v1/verify-admin-password`, {
      method: "POST",
      headers: anonAuthHeaders,
      body: JSON.stringify({ password }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { ok: false, error: data.error || "Verification failed" };
    }
    if (data.ok === true) {
      return { ok: true };
    }
    return { ok: false };
  } catch (e) {
    console.error("verifyAdminPassword:", e);
    return { ok: false, error: "Unable to reach server" };
  }
};

export const recoverUser = async (username) => {
  const response = await fetch(`${url}/functions/v1/forgot-password`, {
    method: "POST",
    headers: anonAuthHeaders,
    body: JSON.stringify({ username }),
  });
  const responseData = await response.json();
  if (responseData?.success) {
    return { success: true };
  }
  return { success: false, error: responseData?.error || "User not found" };
};

// todo: create supabase edge function for this
export const adminFetchUser = async (username) => {
  const response = await fetch(`${url}/functions/v1/admin-recover-user`, {
    method: "POST",
    headers: anonAuthHeaders,
    body: JSON.stringify({ username }),
  });
  const responseData = await response.json();
  if (responseData.link.properties) {
    return responseData.link.properties.action_link;
  }
  return null;
};

const EMAIL_SUFFIX = "@optonyusa.com";

function buildEmailToUser(listUsersData) {
  const map = new Map();
  if (listUsersData && Array.isArray(listUsersData)) {
    for (const user of listUsersData) {
      const e = user.email?.toLowerCase();
      if (e) map.set(e, { id: user.id });
    }
  }
  return map;
}

function lookupByUsernames(emailToUser, usernames) {
  return usernames.map((u) => {
    const email = u + EMAIL_SUFFIX;
    const user = emailToUser.get(email);
    return {
      username: u,
      exists: Boolean(user),
      userId: user?.id ?? null,
    };
  });
}

async function fetchAuthUsersAndLookup(body, toCheck, errorMessage) {
  const response = await fetch(`${url}/functions/v1/check-user-exists`, {
    method: "POST",
    headers: anonAuthHeaders,
    body: JSON.stringify(body),
  });
  const responseData = await response.json();
  if (!response.ok) {
    return { error: responseData.error || errorMessage };
  }
  const emailToUser = buildEmailToUser(responseData.users);
  return lookupByUsernames(emailToUser, toCheck);
}

/**
 * Check if a user exists in Supabase Auth (single username)
 * @param {string} username - The username to check (without @optonyusa.com)
 * @returns {Promise<{exists: boolean, userId?: string|null}|{error: string}>}
 */
export const checkUserExists = async (username) => {
  if (!username) {
    return { error: "Username is required" };
  }
  try {
    const toCheck = [String(username).toLowerCase().trim()];
    const results = await fetchAuthUsersAndLookup(
      { username: toCheck[0] },
      toCheck,
      "Failed to check user existence",
    );
    if (results.error) return results;
    const r = results[0];
    return { exists: r.exists, userId: r.userId };
  } catch (error) {
    console.error("Error checking user existence:", error);
    return { error: error.message || "Unable to verify user existence" };
  }
};

/**
 * Check which usernames exist in Supabase Auth (batch, one request)
 * @param {string[]} usernames - Usernames to check (without @optonyusa.com)
 * @returns {Promise<{results: Array<{username: string, exists: boolean, userId: string|null}>}|{error: string}>}
 */
export const checkUsersExist = async (usernames) => {
  if (!Array.isArray(usernames) || usernames.length === 0) {
    return { error: "usernames array is required" };
  }
  try {
    const toCheck = usernames.map((u) => String(u).toLowerCase().trim()).filter(Boolean);
    const results = await fetchAuthUsersAndLookup({ usernames }, toCheck, "Failed to check users");

    if (results.error) return results;
    return { results };
  } catch (error) {
    console.error("Error checking users existence:", error);
    return { error: error.message || "Unable to verify users" };
  }
};
